/**
 * Reset Password — accepts the token from the email link as a query param,
 * lets the user pick a new password, and posts to /auth/reset-password.
 *
 * When the backend returns "expired" / "already used" / "invalid", we render
 * an inline "Resend reset link" form so the user can recover in one click
 * without bouncing back to /forgot-password.
 */
import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, CheckCircle2, Mail, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errorDetail, setErrorDetail] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters', { duration: 6000 });
      return;
    }
    if (newPassword !== confirm) {
      toast.error('Passwords do not match', { duration: 6000 });
      return;
    }
    // Safe diagnostic — token length & first 4 chars only (never the full token).
    // Lets us read the browser console if a user still hits "invalid".
    try {
      // eslint-disable-next-line no-console
      console.log('[reset] submitting', { token_len: token.length, token_prefix: token.slice(0, 4) });
    } catch (_) { /* noop */ }
    setLoading(true);
    setErrorDetail('');
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      setDone(true);
      toast.success('Password updated. Redirecting to sign in...', { duration: 4000 });
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not reset password. Try requesting a new link.';
      setErrorDetail(detail);
      toast.error(detail, { duration: 8000 });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail || !resendEmail.includes('@')) {
      toast.error('Enter the email you used to register', { duration: 5000 });
      return;
    }
    setResending(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: resendEmail.trim().toLowerCase() });
      setResent(true);
      toast.success('If that email is on file, a fresh link is on the way.', { duration: 6000 });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not send the link. Try again in a minute.';
      toast.error(detail, { duration: 6000 });
    } finally {
      setResending(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0b0d0b] flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md glass-card p-8 text-center">
          <h1 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white mb-4">Invalid link</h1>
          <p className="text-[#a8b0a8] text-sm mb-6">This reset link is missing a token. Please request a new one.</p>
          <Link to="/forgot-password" className="btn-gold inline-block">Request new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0d0b] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <div className="glass-card p-8 md:p-10" data-testid="reset-password-card">
          {!done ? (
            <>
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#c8b273]/15 border border-[#c8b273]/40 mb-4">
                <Lock className="w-6 h-6 text-[#c8b273]" />
              </div>
              <h1 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-extrabold uppercase tracking-wider text-white mb-3">
                Choose a new password
              </h1>
              <p className="text-[#a8b0a8] text-sm mb-6">
                Minimum 8 characters with at least one letter and one number or symbol.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#8a948a] mb-2">New password</label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    autoFocus
                    autoComplete="new-password"
                    data-testid="reset-new-password-input"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#8a948a] mb-2">Confirm new password</label>
                  <Input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    data-testid="reset-confirm-password-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="btn-gold w-full" data-testid="reset-submit-btn">
                  {loading ? 'Updating...' : 'Update password'}
                </Button>
              </form>

              {/* Inline recovery — if the backend rejected the token, let the user
                  re-send a fresh link without leaving the page. */}
              {errorDetail && (
                <div className="mt-6 pt-6 border-t border-[#1f221f]" data-testid="reset-recover-block">
                  <div className="flex items-start gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-[#c0c4c0]">{errorDetail}</p>
                  </div>
                  {!resent ? (
                    <form onSubmit={handleResend} className="space-y-2" data-testid="reset-resend-form">
                      <label className="block text-xs uppercase tracking-wider text-[#8a948a]">Send a fresh link</label>
                      <Input
                        type="email"
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        placeholder="your-email@example.com"
                        required
                        data-testid="reset-resend-email-input"
                      />
                      <Button type="submit" disabled={resending} className="btn-gold w-full" data-testid="reset-resend-btn">
                        <Mail className="w-4 h-4 mr-2" />
                        {resending ? 'Sending...' : 'Resend reset link'}
                      </Button>
                    </form>
                  ) : (
                    <p className="text-sm text-green-400 flex items-center gap-2" data-testid="reset-resend-sent">
                      <CheckCircle2 className="w-4 h-4" /> Check your inbox for the fresh link.
                    </p>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-4" data-testid="reset-success">
              <CheckCircle2 className="w-12 h-12 text-[#c8b273] mx-auto mb-4" />
              <p className="text-white font-semibold mb-2">Password updated</p>
              <p className="text-[#a8b0a8] text-sm">Taking you to sign in...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
